import {
  startAuthentication,
  startRegistration,
} from "@simplewebauthn/browser";
import {
  useIsMutating,
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { useCallback } from "react";
import { api, serverExplained } from "./api";
import {
  cacheMessage,
  cacheOutgoing,
  clearUnread,
  discardOutgoing,
  failOutgoing,
  isChatOpen,
  isCurrentUser,
  keys,
  loadOlderMessages,
  refreshAccount,
  requestSignal,
  sessionId,
  setSession,
  signedOut,
  type OutgoingMessage,
} from "./queries";
import { t } from "../i18n";
import { notify } from "../ui/toast";
import { AuthenticatedSnapshotSchema } from "../../shared/schemas";
import {
  CallLinkResponseSchema,
  DeviceSignInCheckResponseSchema,
  DeviceSignInStartResponseSchema,
  GroupCallResponseSchema,
  GroupJoinResponseSchema,
  MessageResponseSchema,
  OkResponseSchema,
  PasskeyCreationOptionsSchema,
  PasskeyRequestOptionsSchema,
  PersonResponseSchema,
  ProfileLinkResponseSchema,
  RecoveryResponseSchema,
  type ApiRequest,
  type ConnectionAction,
  type PushState,
} from "../../shared/api";
import type {
  Availability,
  Avatar,
  CallLink,
  Person,
  Snapshot,
} from "../../shared/types";

type Body<Action extends ApiRequest["action"]> = Omit<
  Extract<ApiRequest, { action: Action }>,
  "action"
>;

// Read identity when the mutation starts, including a request just after
// joining. The query client's mutation cache answers 401 and 403 from it.
const startedBy = (client: QueryClient) =>
  sessionId(client.getQueryData<Snapshot>(keys.snapshot));
const account = (client: QueryClient) => ({
  mutationKey: keys.actions,
  onMutate: () => startedBy(client),
});

const updateMe = (client: QueryClient, changes: Partial<Person>) =>
  client.setQueryData<Snapshot>(keys.snapshot, (old) =>
    old?.authenticated ? { ...old, me: { ...old.me, ...changes } } : old,
  );

// Any account action in flight holds back the controls that start another.
export const useSubmitting = () =>
  useIsMutating({ mutationKey: keys.actions }) > 0;

export function useRegister() {
  const client = useQueryClient();
  return useMutation({
    ...account(client),
    mutationFn: (body: Extract<ApiRequest, { action: "register" }>) =>
      api(AuthenticatedSnapshotSchema, body),
    onSuccess: (data) => setSession(client, data),
  });
}

export function useRestore() {
  const client = useQueryClient();
  return useMutation({
    ...account(client),
    mutationFn: (code: string) =>
      api(AuthenticatedSnapshotSchema, { action: "restore", code }),
    onSuccess: (data) => setSession(client, data),
  });
}

// Closing the browser's own prompt is no failure, so it gives back nothing.
async function askBrowser<T>(ask: () => Promise<T>) {
  try {
    return await ask();
  } catch (error) {
    const { name } = error as Error;
    if (name === "NotAllowedError" || name === "AbortError") return null;
    throw new Error(
      name === "InvalidStateError"
        ? "هذا الجهاز يحمل مفتاح مرور لحسابك بالفعل."
        : "تعذر استخدام مفتاح المرور على هذا الجهاز.",
      { cause: error },
    );
  }
}

export function usePasskeySignIn() {
  const client = useQueryClient();
  return useMutation({
    ...account(client),
    mutationFn: async () => {
      const optionsJSON = await api(PasskeyRequestOptionsSchema, {
        action: "passkey-sign-in-options",
      });
      const credential = await askBrowser(() =>
        startAuthentication({ optionsJSON }),
      );
      return (
        credential &&
        api(AuthenticatedSnapshotSchema, {
          action: "passkey-sign-in",
          credential,
        })
      );
    },
    onSuccess: (data) => {
      if (data) setSession(client, data);
    },
  });
}

const deviceCheckEveryMs = 3000;

// Gets a code for this device, then waits for another device to approve it, and
// answers with the account or with nothing once the request is gone. The token
// claims the session, so it stays in this call's memory, out of every cache. This
// is no mutation, so waiting holds back no other control. One failed check ends
// nothing: the next one tries again, and the server says when the time is up.
export function useWaitForApproval() {
  const client = useQueryClient();
  return useCallback(
    async (signal: AbortSignal, onCode: (code: string) => void) => {
      const { code, token } = await api(
        DeviceSignInStartResponseSchema,
        { action: "device-sign-in-start" },
        "",
        requestSignal(signal),
      );
      onCode(code);
      for (;;) {
        await new Promise((resolve) => setTimeout(resolve, deviceCheckEveryMs));
        if (signal.aborted) return null;
        try {
          const result = await api(
            DeviceSignInCheckResponseSchema,
            { action: "device-sign-in-check", token },
            "",
            requestSignal(signal),
          );
          if (result.authenticated) {
            setSession(client, result);
            return result;
          }
          if (result.state === "gone") return null;
        } catch {
          continue;
        }
      }
    },
    [client],
  );
}

export function useApproveDevice() {
  const client = useQueryClient();
  return useMutation({
    ...account(client),
    mutationFn: (code: string) =>
      api(OkResponseSchema, { action: "device-sign-in-approve", code }),
  });
}

export function useAddPasskey() {
  const client = useQueryClient();
  return useMutation({
    ...account(client),
    mutationFn: async () => {
      const optionsJSON = await api(PasskeyCreationOptionsSchema, {
        action: "passkey-register-options",
      });
      const credential = await askBrowser(() =>
        startRegistration({ optionsJSON }),
      );
      return (
        credential &&
        api(OkResponseSchema, { action: "passkey-register", credential })
      );
    },
    onSuccess: (data, _variables, accountId) =>
      data && accountId ? refreshAccount(client, accountId) : undefined,
  });
}

export function useRemovePasskey() {
  const client = useQueryClient();
  return useMutation({
    ...account(client),
    mutationFn: (id: string) =>
      api(OkResponseSchema, { action: "passkey-delete", id }),
    onSuccess: (_data, _variables, accountId) =>
      accountId ? refreshAccount(client, accountId) : undefined,
  });
}

export function useEndSession() {
  const client = useQueryClient();
  return useMutation({
    ...account(client),
    mutationFn: (id: string) =>
      api(OkResponseSchema, { action: "session-end", id }),
    onSuccess: (_data, _variables, accountId) =>
      accountId ? refreshAccount(client, accountId) : undefined,
  });
}

export function useEndOtherSessions() {
  const client = useQueryClient();
  return useMutation({
    ...account(client),
    mutationFn: () => api(OkResponseSchema, { action: "session-end-others" }),
    onSuccess: (_data, _variables, accountId) =>
      accountId ? refreshAccount(client, accountId) : undefined,
  });
}

export function useConnection() {
  const client = useQueryClient();
  return useMutation({
    ...account(client),
    mutationFn: (body: {
      action: ConnectionAction;
      peer: string;
      code?: string;
    }) => api(OkResponseSchema, body),
    onSuccess: (_data, _variables, accountId) =>
      accountId ? refreshAccount(client, accountId) : undefined,
  });
}

export function useSendMessage() {
  const client = useQueryClient();
  return useMutation({
    // Sending never disables controls, and messages leave in the order written.
    mutationKey: keys.send,
    scope: { id: "send-message" },
    onMutate: (outgoing: OutgoingMessage) => {
      const accountId = startedBy(client);
      if (accountId) cacheOutgoing(client, accountId, outgoing);
      return accountId;
    },
    mutationFn: (outgoing: OutgoingMessage) =>
      api(
        MessageResponseSchema,
        { action: "message", ...outgoing },
        "",
        requestSignal(),
      ),
    onSuccess: (data, _variables, accountId) => {
      if (accountId) cacheMessage(client, accountId, data.message);
    },
    onError(error, outgoing, accountId) {
      if (accountId) failOutgoing(client, accountId, outgoing);
      // An open chat shows a lost connection on the message itself.
      if (!accountId || !isChatOpen(client, accountId, outgoing.peer)) {
        const data = client.getQueryData<Snapshot>(keys.snapshot);
        const name =
          data?.authenticated &&
          data.friends.find((friend) => friend.id === outgoing.peer)?.name;
        notify(name ? t("لم تُرسل رسالتك إلى {name}", { name }) : error.message);
      } else if (serverExplained(error)) notify(error.message);
    },
  });
}

export function useLoadOlderMessages(userId: string, peer: string) {
  const client = useQueryClient();
  return useMutation({
    // Reading further back never disables the rest of the app.
    onMutate: () => startedBy(client),
    mutationFn: () => loadOlderMessages(client, userId, peer),
  });
}

export function useDiscardMessage() {
  const client = useQueryClient();
  return (outgoing: OutgoingMessage) => {
    const accountId = startedBy(client);
    if (accountId) discardOutgoing(client, accountId, outgoing);
  };
}

export function useMarkRead() {
  const client = useQueryClient();
  return useMutation({
    // Marking a chat read happens in the background and never disables controls.
    mutationKey: keys.read,
    onMutate: (peer: string) => {
      clearUnread(client, peer);
      return startedBy(client);
    },
    mutationFn: (peer: string) =>
      api(OkResponseSchema, { action: "read", peer }),
  });
}

export function useReport() {
  const client = useQueryClient();
  return useMutation({
    ...account(client),
    mutationFn: async ({
      peer,
      reason,
      block,
    }: {
      peer: string;
      reason: string;
      block: boolean;
    }) => {
      // Apply protection first, even if the report is rejected or fails.
      if (block) await api(OkResponseSchema, { action: "block", peer });
      return api(OkResponseSchema, {
        action: "report",
        peer,
        reason,
        confirmed: true,
      });
    },
    onSettled: (_data, _error, { block }, accountId) =>
      block && accountId ? refreshAccount(client, accountId) : undefined,
  });
}

export function useRemoveRecent() {
  const client = useQueryClient();
  return useMutation({
    ...account(client),
    mutationFn: (callId: string) =>
      api(OkResponseSchema, { action: "remove-recent", callId }),
    onSuccess: (_data, _variables, accountId) =>
      accountId ? refreshAccount(client, accountId) : undefined,
  });
}

// Status, last-active sharing and being found by username apply as they are
// chosen, so the control shows the choice at once and a failure restores the
// account's own state.
function useChoice<Choice>(
  mutationFn: (choice: Choice) => Promise<unknown>,
  shown: (choice: Choice) => Partial<Person>,
) {
  const client = useQueryClient();
  return useMutation({
    mutationKey: keys.actions,
    onMutate: (choice: Choice) => {
      const accountId = startedBy(client);
      updateMe(client, shown(choice));
      return accountId;
    },
    mutationFn,
    onError(_error, _choice, accountId) {
      if (accountId) void refreshAccount(client, accountId);
    },
  });
}

export const useSetStatus = () =>
  useChoice(
    (status: Availability) =>
      api(OkResponseSchema, { action: "status", status }),
    (status) => ({ status }),
  );

export const useSetLastActive = () =>
  useChoice(
    (showLastActive: boolean) =>
      api(OkResponseSchema, { action: "last-active", showLastActive }),
    (showLastActive) => ({ show_last_active: showLastActive ? 1 : 0 }),
  );

export const useSetDiscoverable = () =>
  useChoice(
    (discoverable: boolean) =>
      api(OkResponseSchema, { action: "discoverable", discoverable }),
    (discoverable) => ({ discoverable: discoverable ? 1 : 0 }),
  );

// Made the first time the profile link is shared; a reset replaces it.
export function useShareProfile() {
  const client = useQueryClient();
  return useMutation({
    ...account(client),
    mutationFn: (reset: boolean) =>
      api(ProfileLinkResponseSchema, { action: "profile-link", reset }),
    onSuccess: (data, _reset, accountId) => {
      if (accountId && isCurrentUser(client, accountId))
        updateMe(client, { link_code: data.code });
    },
  });
}

export function useSaveProfile() {
  const client = useQueryClient();
  return useMutation({
    ...account(client),
    mutationFn: (body: Body<"profile">) =>
      api(OkResponseSchema, { action: "profile", ...body }),
    onSuccess: (_data, _variables, accountId) =>
      accountId ? refreshAccount(client, accountId) : undefined,
  });
}

export function useSaveAvatar() {
  const client = useQueryClient();
  return useMutation({
    ...account(client),
    mutationFn: (avatar: Avatar | null) =>
      api(OkResponseSchema, { action: "avatar", avatar }),
    onSuccess: (_data, avatar, accountId) => {
      if (accountId && isCurrentUser(client, accountId))
        updateMe(client, { avatar });
    },
  });
}

export function useCreateRecovery() {
  const client = useQueryClient();
  return useMutation({
    ...account(client),
    mutationFn: () =>
      api(RecoveryResponseSchema, { action: "create-recovery" }),
    onSuccess: (_data, _variables, accountId) => {
      if (accountId && isCurrentUser(client, accountId))
        updateMe(client, { has_recovery: 1 });
    },
  });
}

const setLink = (
  client: QueryClient,
  accountId: string | null | undefined,
  link: CallLink | null,
) => {
  if (accountId && isCurrentUser(client, accountId))
    client.setQueryData<Snapshot>(keys.snapshot, (old) =>
      old?.authenticated ? { ...old, link } : old,
    );
};

export function useCreateCallLink() {
  const client = useQueryClient();
  return useMutation({
    ...account(client),
    mutationFn: () => api(CallLinkResponseSchema, { action: "create-link" }),
    onSuccess: (data, _variables, accountId) =>
      setLink(client, accountId, data.link),
  });
}

export function useCancelCallLink() {
  const client = useQueryClient();
  return useMutation({
    ...account(client),
    mutationFn: () => api(OkResponseSchema, { action: "cancel-link" }),
    onSuccess: (_data, _variables, accountId) =>
      setLink(client, accountId, null),
  });
}

// A visitor without an account becomes a guest, so the session changes too.
export function useJoinCallLink() {
  const client = useQueryClient();
  return useMutation({
    ...account(client),
    mutationFn: (body: Body<"join-link">) =>
      api(PersonResponseSchema, { action: "join-link", ...body }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.snapshot }),
  });
}

export function useStartGroupCall() {
  const client = useQueryClient();
  return useMutation({
    ...account(client),
    mutationFn: () => api(GroupCallResponseSchema, { action: "group-start" }),
  });
}

export function useJoinGroupCall() {
  const client = useQueryClient();
  return useMutation({
    ...account(client),
    mutationFn: (body: Body<"group-join">) =>
      api(GroupJoinResponseSchema, { action: "group-join", ...body }),
    onSuccess: ({ session }) => {
      if (session) setSession(client, session);
    },
  });
}

export function useEndGroupCall() {
  const client = useQueryClient();
  return useMutation({
    ...account(client),
    mutationFn: (body: Body<"group-end">) =>
      api(OkResponseSchema, { action: "group-end", ...body }),
  });
}

export function useKeep() {
  const client = useQueryClient();
  return useMutation({
    ...account(client),
    mutationFn: (body: Body<"keep">) =>
      api(AuthenticatedSnapshotSchema, { action: "keep", ...body }),
    onSuccess: (data) => setSession(client, data),
  });
}

const setPush = (
  client: QueryClient,
  accountId: string | null | undefined,
  subscription: PushState["subscription"],
) => {
  if (accountId && isCurrentUser(client, accountId))
    client.setQueryData<PushState>(
      keys.push(accountId),
      (old) => old && { ...old, subscription },
    );
};

// Notification settings change in the background and never disable controls.
export function useSubscribePush() {
  const client = useQueryClient();
  return useMutation({
    ...account(client),
    mutationKey: keys.pushActions,
    mutationFn: (body: Body<"push-subscribe">) =>
      api(OkResponseSchema, { action: "push-subscribe", ...body }),
    onSuccess: (_data, body, accountId) =>
      setPush(client, accountId, {
        endpoint: body.subscription.endpoint,
        lang: body.lang,
      }),
  });
}

export function useUnsubscribePush() {
  const client = useQueryClient();
  return useMutation({
    ...account(client),
    mutationKey: keys.pushActions,
    mutationFn: () => api(OkResponseSchema, { action: "push-unsubscribe" }),
    onSuccess: (_data, _variables, accountId) =>
      setPush(client, accountId, null),
  });
}

export function useLogout() {
  const client = useQueryClient();
  return useMutation({
    ...account(client),
    mutationFn: () => api(OkResponseSchema, { action: "logout" }),
    onSuccess: (_data, _variables, accountId) => {
      if (accountId && isCurrentUser(client, accountId))
        setSession(client, signedOut);
    },
  });
}

export function useDeleteAccount() {
  const client = useQueryClient();
  return useMutation({
    ...account(client),
    mutationFn: () => api(OkResponseSchema, { action: "delete-account" }),
    onSuccess: (_data, _variables, accountId) => {
      if (accountId && isCurrentUser(client, accountId))
        setSession(client, signedOut);
    },
  });
}
